import React from 'react';

import { useMutation } from '@tanstack/react-query';
import { useCompleteModal } from 'modal.react.js';
import { omit, pick } from 'ramda';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { ETripEvent, TTripEvent } from 'detailsv2/structure';
import { useModalClose } from 'hooks/useModalClose';
import { showErrorSnackbar, showSuccessSnackbar } from 'hooks/useSnackbar';
import { accountIdSelector } from 'store/user/selectors';
import axiosApi from 'utils/axios';
import { capitalize, reverseFlightDateValues } from 'utils/common';
import { isIDL } from 'utils/date';

import { CustomSubmitHandler, IEditModalProps, SaveHandlerProps, SaveInfoForLaterMutationReq } from './types';

export function useEventModal({ tripEvent }: IEditModalProps) {
  useModalClose();
  const res = useCompleteModal<TTripEvent[] | false>();
  const accountId = useSelector(accountIdSelector);

  const saveInfoForLaterMutation = useMutation({
    mutationFn: ({
      eventType,
      defaultValues,
      pageId,
      pageName,
      wholeTeam,
      transportationType,
    }: SaveInfoForLaterMutationReq) =>
      axiosApi.post(`/acc/${accountId}/trip_event_default_values`, {
        eventType,
        defaultValues,
        pageId,
        pageName,
        wholeTeam,
        transportationType,
      }),
    onSuccess: () => {
      showSuccessSnackbar('Changes saved for later');
    },
  });

  const saveForm: SubmitHandler<FieldValues> = React.useCallback(
    (formData) => {
      const getFormattedTripEvent = (data: FieldValues): TTripEvent => {
        const handledData = omit(['attachedPage'], data);
        const attachedPages = data.attachedPage ? [{ ...data.attachedPage, id: data?.attachedPage?.id || null }] : [];

        const tripEventWithPositions = { ...tripEvent, positions: [0], ...handledData, attachedPages };

        return tripEvent.data?.isIDL || isIDL(tripEvent.daysRange.start, tripEvent.daysRange.end)
          ? reverseFlightDateValues(tripEventWithPositions, true)
          : tripEventWithPositions;
      };

      const data = Array.isArray(formData) ? formData : [formData];

      // The code now works with arrays only after implementing the API flight multiselect feature
      res.success(data.map(getFormattedTripEvent));
    },
    [res, tripEvent],
  );

  const saveInfoForLater = React.useCallback(
    async (props: SaveHandlerProps) => {
      const { fieldValues: defaultFieldValues, eventType } = props;

      // condition for flightAPI which can include array of fieldValues with same default values
      const fieldValues = Array.isArray(defaultFieldValues) ? defaultFieldValues?.[0] : defaultFieldValues;

      const handledDefaultValuesData = (() => {
        let handledData = fieldValues.data;

        if (eventType === ETripEvent.Transfer) {
          handledData = omit(
            ['departureTime', 'departureFrom', 'arrivalTo', 'arrivalTime', 'transportationType'],
            handledData,
          );
        }

        if (eventType === ETripEvent.Flight) {
          handledData = pick(['notes', 'displaySettings', 'isCollapsible'], handledData);
        }

        if (eventType === ETripEvent.Accommodation) {
          handledData = omit(['isAlternate'], handledData);
        }

        handledData = omit(['confirmationNumber'], handledData);

        return handledData;
      })();

      const handledDefaultValues = {
        data: handledDefaultValuesData,
        icon: fieldValues.icon,
      };

      const transportationType = fieldValues?.data?.transportationType;

      await saveInfoForLaterMutation.mutateAsync({ ...props, transportationType, defaultValues: handledDefaultValues });
    },
    [saveInfoForLaterMutation],
  );

  const submitForm: CustomSubmitHandler = async (fieldValues, _event, saveHandlerProps) => {
    try {
      if (saveHandlerProps?.eventType) {
        await saveInfoForLater({ ...saveHandlerProps, fieldValues });
      }

      await saveForm(fieldValues);
    } catch (error) {
      showErrorSnackbar(GENERAL_ERROR_MESSAGE);
    }
  };

  const tripEventName = tripEvent.type === ETripEvent.Transfer ? 'Transportation' : capitalize(tripEvent.type);
  const modalHeader = tripEvent.id ? `Edit ${tripEventName}` : `Add ${tripEventName} to Your Itinerary`;

  return { modalHeader, submitForm, res };
}
